import { useMemo, useState, useRef } from "react"
import { createPortal } from "react-dom"
import { useNavigate } from "react-router"
import { ScheduledPost, PostType } from "../../types/scheduled-post"
import Modal from "../Modal"
import { getAccountColor } from "../../utils/accountColors"

const POST_TYPE_LABELS: Record<PostType, string> = {
  feed:     "フィード",
  story:    "ストーリー",
  reel:     "リール",
  carousel: "カルーセル",
}

const POST_TYPE_ICONS: Record<PostType, string> = {
  feed:     "bi-image",
  story:    "bi-circle",
  reel:     "bi-play-btn",
  carousel: "bi-images",
}

const WEEKDAY_LABELS = ["日", "月", "火", "水", "木", "金", "土"]
const MAX_VISIBLE_PER_DAY = 2
const POPOVER_WIDTH = 260
const POPOVER_HEIGHT_EST = 170  // 位置計算用の推定値

type Account = { id: number; username: string }

type CalendarViewProps = {
  year: number
  month: number
  posts: ScheduledPost[]
  allAccounts: Account[]
  selectedAccountIds: number[]
}

type CalendarDay = {
  date: Date
  isCurrentMonth: boolean
  isToday: boolean
  posts: ScheduledPost[]
}

const formatTime = (iso: string | null): string => {
  if (!iso) return ""
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
}

const formatPopoverDate = (iso: string): string => {
  const d = new Date(iso)
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`
}

const truncateCaption = (caption: string | null): string | null => {
  if (!caption) return null
  return caption.length > 80 ? caption.slice(0, 80) + "…" : caption
}

type PostChipProps = {
  post: ScheduledPost
  allAccounts: Account[]
  onClick: (post: ScheduledPost) => void
}

const PostChip = ({ post, allAccounts, onClick }: PostChipProps) => {
  const chipRef      = useRef<HTMLDivElement>(null)
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null)

  const bgColor     = post.status === "failed" ? "#6b7280" : getAccountColor(post.instagram_account.id, allAccounts)
  const isVideo     = post.media_attachments[0]?.content_type.startsWith("video/") ?? false
  const isClickable = post.status === "scheduled" || post.status === "published"
  const caption     = truncateCaption(post.caption)

  const openPopover = () => {
    if (!chipRef.current) return
    const rect = chipRef.current.getBoundingClientRect()
    let top  = rect.bottom + 6
    let left = rect.left
    if (top + POPOVER_HEIGHT_EST > window.innerHeight) top  = rect.top - POPOVER_HEIGHT_EST - 6
    if (left + POPOVER_WIDTH     > window.innerWidth)  left = window.innerWidth - POPOVER_WIDTH - 8
    if (left < 8) left = 8
    if (top  < 8) top  = 8
    setPopoverPos({ top, left })
  }

  const scheduleOpen = () => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null }
    openTimerRef.current = setTimeout(openPopover, 200)
  }

  const scheduleClose = () => {
    if (openTimerRef.current) { clearTimeout(openTimerRef.current); openTimerRef.current = null }
    closeTimerRef.current = setTimeout(() => setPopoverPos(null), 100)
  }

  const cancelClose = () => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null }
  }

  return (
    <>
      <div
        ref={chipRef}
        className={`post-chip${isClickable ? " post-chip--clickable" : ""}`}
        style={{ background: bgColor }}
        onClick={isClickable ? () => onClick(post) : undefined}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
      >
        <div className="post-chip__thumb-wrap">
          {post.media_attachments[0]?.url ? (
            isVideo ? (
              <video className="post-chip__thumb" src={post.media_attachments[0]?.url} autoPlay loop muted playsInline />
            ) : (
              <img className="post-chip__thumb" src={post.media_attachments[0]?.url} alt="" />
            )
          ) : (
            <i className={`bi ${POST_TYPE_ICONS[post.post_type]}`} style={{ fontSize: 10 }} />
          )}
        </div>
        <span className="post-chip__time">{formatTime(post.scheduled_at)}</span>
        <span className="post-chip__label">{post.title ?? "タイトルなし"}</span>
        {post.status === "publishing" && (
          <span className="post-chip__status-overlay">
            <span className="spinner" />
            <span>アップロード中</span>
          </span>
        )}
        {post.status === "failed" && (
          <span className="post-chip__status-overlay">
            <i className="bi bi-exclamation-circle" />
          </span>
        )}
      </div>

      {popoverPos && createPortal(
        <div
          className={`post-popover${isClickable ? " post-popover--clickable" : ""}`}
          style={{ top: popoverPos.top, left: popoverPos.left, width: POPOVER_WIDTH }}
          onMouseEnter={cancelClose}
          onMouseLeave={() => setPopoverPos(null)}
          onClick={isClickable ? () => onClick(post) : undefined}
        >
          <div className="post-popover__body">
            <div className="post-popover__thumb-wrap">
              {post.media_attachments[0]?.url ? (
                isVideo ? (
                  <video className="post-popover__thumb" src={post.media_attachments[0]?.url} autoPlay loop muted playsInline />
                ) : (
                  <img className="post-popover__thumb" src={post.media_attachments[0]?.url} alt="" />
                )
              ) : (
                <i className={`bi ${POST_TYPE_ICONS[post.post_type]}`} style={{ fontSize: 24, color: "#9ca3af" }} />
              )}
            </div>
            <div className="post-popover__info">
              {post.scheduled_at && (
                <>
                  <div className="post-popover__date">{formatPopoverDate(post.scheduled_at)}</div>
                  <div className="post-popover__time">{formatTime(post.scheduled_at)}</div>
                </>
              )}
              <div className="post-popover__title">{post.title ?? "タイトルなし"}</div>
              <div className="post-popover__type">{POST_TYPE_LABELS[post.post_type]}</div>
            </div>
          </div>
          {caption && (
            <div className="post-popover__caption">{caption}</div>
          )}
          {post.status === "failed" && post.error_message && (
            <div className="post-popover__error">
              <i className="bi bi-exclamation-circle-fill" />
              {post.error_message}
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  )
}

type DayModalProps = {
  day: CalendarDay
  onClose: () => void
  allAccounts: Account[]
  onChipClick: (post: ScheduledPost) => void
}

const DayModal = ({ day, onClose, allAccounts, onChipClick }: DayModalProps) => {
  const title = `${day.date.getMonth() + 1}月${day.date.getDate()}日の予約投稿`
  return (
    <Modal title={title} onClose={onClose} size="lg">
      <div className="calendar-day-modal__posts">
        {day.posts.map(post => (
          <PostChip key={post.id} post={post} allAccounts={allAccounts} onClick={onChipClick} />
        ))}
      </div>
    </Modal>
  )
}

const CalendarView = ({ year, month, posts, allAccounts, selectedAccountIds }: CalendarViewProps) => {
  const navigate = useNavigate()
  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null)

  const handleChipClick = (post: ScheduledPost) => {
    if (post.status === "scheduled") {
      navigate(`/post-schedule/${post.id}`)
      return
    }
    if (post.status === "published" && post.post_id) {
      const accountId = post.instagram_account.id
      if (post.post_type === "reel")       navigate(`/reels/${post.post_id}`, { state: { accountId } })
      else if (post.post_type === "story") navigate(`/stories/${post.post_id}`, { state: { accountId } })
      else                                 navigate(`/feed-posts/${post.post_id}`, { state: { accountId } })
    }
  }

  const days = useMemo<CalendarDay[]>(() => {
    const firstDay = new Date(year, month - 1, 1)
    const lastDay  = new Date(year, month, 0)

    const startDate = new Date(firstDay)
    startDate.setDate(startDate.getDate() - startDate.getDay())

    const endDate = new Date(lastDay)
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()))

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const result: CalendarDay[] = []
    const cur = new Date(startDate)

    while (cur <= endDate) {
      const pad = (n: number) => String(n).padStart(2, "0")
      const dateStr = `${cur.getFullYear()}-${pad(cur.getMonth() + 1)}-${pad(cur.getDate())}`
      const dayPosts = posts.filter(p => {
        if (!p.scheduled_at) return false
        const d = new Date(p.scheduled_at)
        const localStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
        return localStr === dateStr
      }).sort((a, b) => {
        if (!a.scheduled_at || !b.scheduled_at) return 0
        return a.scheduled_at.localeCompare(b.scheduled_at)
      })

      result.push({
        date:           new Date(cur),
        isCurrentMonth: cur.getMonth() === month - 1,
        isToday:        cur.getTime() === today.getTime(),
        posts:          dayPosts,
      })

      cur.setDate(cur.getDate() + 1)
    }

    return result
  }, [year, month, posts])

  const visibleAccounts = allAccounts.filter(a => selectedAccountIds.includes(a.id))

  return (
    <div>
      {visibleAccounts.length > 0 && (
        <div className="calendar__legend">
          {visibleAccounts.map(a => (
            <div key={a.id} className="calendar__legend-item">
              <span
                className="calendar__legend-dot"
                style={{ background: getAccountColor(a.id, allAccounts) }}
              />
              <span>{a.username}</span>
            </div>
          ))}
        </div>
      )}

      <div className="calendar__weekdays">
        {WEEKDAY_LABELS.map(d => (
          <div key={d}>{d}</div>
        ))}
      </div>

      <div className="calendar__grid">
        {days.map((day, i) => {
          const cellClass = [
            "calendar__cell",
            !day.isCurrentMonth ? "calendar__cell--other-month" : "",
            day.isToday ? "calendar__cell--today" : "",
          ].filter(Boolean).join(" ")

          const visiblePosts  = day.posts.slice(0, MAX_VISIBLE_PER_DAY)
          const overflowCount = day.posts.length - MAX_VISIBLE_PER_DAY

          return (
            <div key={i} className={cellClass}>
              <div className="calendar__day">{day.date.getDate()}</div>
              <div className="calendar__posts">
                {visiblePosts.map(post => (
                  <PostChip key={post.id} post={post} allAccounts={allAccounts} onClick={handleChipClick} />
                ))}
              </div>
              {overflowCount > 0 && (
                <button
                  className="calendar__overflow"
                  onClick={() => setSelectedDay(day)}
                >
                  +{overflowCount}件
                </button>
              )}
            </div>
          )
        })}
      </div>

      {selectedDay && (
        <DayModal
          day={selectedDay}
          onClose={() => setSelectedDay(null)}
          allAccounts={allAccounts}
          onChipClick={handleChipClick}
        />
      )}
    </div>
  )
}

export default CalendarView
